import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/_ui/Accordion'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/_ui/Dialog'
import T from '@/components/Helpers/Translate'
import { cn } from '@/lib/utils'
import useGlobalStore from '@/store/useGlobalStore'
import { Accordion } from '@radix-ui/react-accordion'
import React from 'react'
import { ErrorAlert } from '@/components/_ui/ErrorAlert'
import type { CartItemType } from '@/store/types/cart'
import { Button } from '@/components/_ui/Button'
import Spinner from '@/components/_ui/Spinner'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { getSubscriptionNextShippingDate } from '../_helpers/getSubscriptionNextShippingDate'
import { useGetSelectedSubscription } from '../_helpers/hooks/useGetSelectedSubscription'
import { SubscriptionAccordionLineItem } from '../_helpers/components/SubscriptionAccordionLineItem'
import { ConfirmAddToActiveSubscriptionTotals } from './ConfirmAddToActiveSubscriptionTotals'
import { useCalculateNewSubscriptionTotals } from '../_helpers/hooks/useCalculateNewSubscriptionTotals'
import { useAddNewItemToSubscription } from '../_helpers/hooks/useAddNewItemToSubscription'

type Props = {
  itemToAddToSubscription: CartItemType
}

export const ConfirmAddToActiveSubscription = ({
  itemToAddToSubscription,
}: Props) => {
  const {
    app: {
      addProductToSubscription,
      addProductToSubscription: { selectedSubscriptionIndex },
    },

    setAppAddProductToSubscription,
  } = useGlobalStore()

  const { selectedSubscription } = useGetSelectedSubscription()
  const { orderCalculation, isLoadingOrderCalculation } =
    useCalculateNewSubscriptionTotals({
      subscription: selectedSubscription,
      itemToAddToSubscription,
    })

  const { handleAddItemToSubscription, isLoadingAddingItemToSubscription } =
    useAddNewItemToSubscription()

  if (!selectedSubscription) {
    return (
      <ErrorAlert
        error={{
          error: {
            message: 'Something went wrong, try again later',
          },
        }}
      />
    )
  }

  return (
    <Dialog
      onOpenChange={(open) => {
        setAppAddProductToSubscription({
          isConfirmAddToActiveSubscriptionModalOpen: open,
        })
      }}
      open={addProductToSubscription.isConfirmAddToActiveSubscriptionModalOpen}
    >
      <DialogContent className="w-full max-w-2xl p-1 py-5 sm:p-8">
        <DialogHeader>
          <DialogTitle>
            <T>review_subscription_changes</T>
          </DialogTitle>
        </DialogHeader>
        <div>
          <Accordion
            className="w-full"
            type="single"
            defaultChecked={true}
            value="subscription"
          >
            <div className="mt-3 flex flex-col">
              <AccordionItem value="subscription">
                <AccordionTrigger
                  className={cn(
                    'h-13 border-1 rounded-lg border border-solid border-[#99BCDF] py-3 pr-3 text-sm font-semibold hover:no-underline',
                  )}
                >
                  <div className="m-0 flex items-center space-x-2 pl-3 ">
                    <div className="w-full flex-col items-center justify-between md:flex md:flex-row md:space-x-1">
                      <div className="flex space-x-1">
                        <T>subscription</T>
                        <span>{selectedSubscriptionIndex}</span>
                      </div>
                      <span className="hidden md:block">-</span>
                      <div className="flex space-x-1 font-light">
                        <span>
                          <T>next_ship_date</T>
                        </span>
                        <span>:</span>
                        <span>
                          {getSubscriptionNextShippingDate({
                            subscription: selectedSubscription,
                          })}{' '}
                        </span>
                      </div>
                      <span className="hidden font-light md:block">|</span>
                      <div className="flex font-light md:max-w-[25%]">
                        <span className="truncate">
                          {selectedSubscription.shipToAddress.address1}
                        </span>
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="mx-2 rounded-b-lg bg-[#99bcdf26] p-3.5 px-5 md:p-5 md:px-10">
                    {selectedSubscription.lines.items.map((line) => {
                      if (!line.productData) return null

                      return (
                        <SubscriptionAccordionLineItem
                          key={line.item.id.unicity}
                          line={line}
                        />
                      )
                    })}
                    <div
                      className="my-2 space-x-1 "
                      key={itemToAddToSubscription.product.sku}
                    >
                      <span className="font-bold">
                        ({itemToAddToSubscription.qty})
                      </span>
                      <span className="font-bold">
                        {itemToAddToSubscription.title}
                      </span>

                      <span>-</span>
                      <span>
                        {itemToAddToSubscription.product.combinationName}
                      </span>
                      <span className="mx-2 rounded-md bg-green-700 px-2 py-1 text-xs uppercase text-white">
                        <T>new</T>
                      </span>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </div>
          </Accordion>
          <ConfirmAddToActiveSubscriptionTotals
            orderCalculation={orderCalculation}
            isLoadingOrderCalculation={isLoadingOrderCalculation}
            subscription={selectedSubscription}
            itemToAddToSubscription={itemToAddToSubscription}
          />
          <div className="m-auto mt-5 flex w-full max-w-sm flex-col justify-center space-y-3">
            <Button
              onClick={() => {
                mixpanelService
                  .trackEvent(
                    MixpanelEvent.REVIEW_SUBSCRIPTION_CHANGES_CONFIRM_CLICKED,
                    {
                      subscription_id: selectedSubscription.id.unicity,
                    },
                  )
                  .catch(() => {})
                handleAddItemToSubscription({
                  subscription: selectedSubscription,
                  itemToAddToSubscription,
                  orderCalculation,
                })
              }}
              disabled={
                isLoadingOrderCalculation ||
                !!orderCalculation?.error ||
                isLoadingAddingItemToSubscription
              }
              variant="secondary"
            >
              {isLoadingAddingItemToSubscription ? <Spinner /> : <T>confirm</T>}
            </Button>
            <Button
              className="capitalize"
              onClick={() => {
                setAppAddProductToSubscription({
                  isConfirmAddToActiveSubscriptionModalOpen: false,
                  isSelectActiveSubscriptionModalOpen: true,
                })
              }}
              variant="ghost"
            >
              <T>back</T>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
